import * as THREE from "three";
import { CosmeticPicker, RANDOM, type AirTrailId, type FinisherId, type GrindSparksId, type LandingFxId, type Random } from "../settings";
import type { SkaterEvent } from "../sim/state";
import type { Cue } from "../sound/cues";
import { rng } from "./bail-fx";

// Trick effects (#118): sparks off the trucks on a grind, a trail behind
// the board in the air, a burst where you land and a finisher when a big
// combo lands (fireworks here; `update` says which went off, so `Game` can
// play the crowd cheer or start the slow-mo zoom), each in a style picked
// in Settings, or a fresh pick each time one plays on Random (#128: each
// grind, air, landing and big combo). Render-only: it reads the
// skater and the step's sound cues and never writes back, so no choice
// changes the sim. Every particle lives in a fixed pool per shape, one
// InstancedMesh each, made once: emitting past a pool's cap drops the new
// particle, so nothing grows or allocates while you skate (cheap on phones).

/** What the effects need from the skater each frame. */
export interface FxSkater {
  mode: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vz: number;
}

/** How many of each shape can be alive at once. */
export const FX_CAPS = { spark: 400, puff: 64, ring: 8, splat: 48, note: 32, streak: 48 } as const;
type LayerId = keyof typeof FX_CAPS;

/** Sparks per second per m/s of grind speed. */
const SPARK_RATE = 14;
/** A Perfect landing's burst, against a clean one's. */
const PERFECT_BOOST = 1.7;
/** How high above the rail's line the trucks are. */
const TRUCK_Y = 0.08;
/** The ribbon's length in frames' worth of points, and how long a point lasts. */
export const TRAIL_POINTS = 40;
const TRAIL_LIFE = 0.35;
const TRAIL_WIDTH = 0.28;
/** Height of the board's middle above the skater's position. */
const BOARD_Y = 0.12;
/** A landed combo worth this much sets off the finisher. */
export const BIG_COMBO = 10_000;
const FIREWORK_COLORS = ["#ff4fb8", "#ffe14d", "#2ec4b6", "#7fe8ff", "#ff6b35", "#ffffff"];

interface Emit {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  /** Size at birth and at death. */
  size: number;
  end: number;
  /** Up is positive: a negative `g` floats. */
  g: number;
  drag: number;
  /** Yaw, and how fast it turns (rad/s). */
  rot: number;
  spin: number;
  color: THREE.Color;
}

/** One shape's pool: particle state in flat arrays, drawn by one InstancedMesh. */
class Layer {
  readonly mesh: THREE.InstancedMesh;
  n = 0;
  private readonly f: Float32Array;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler(0, 0, 0, "YXZ");
  private readonly pos = new THREE.Vector3();
  private readonly scale = new THREE.Vector3();
  private readonly c = new THREE.Color();

  /** Floats per particle: x y z, vx vy vz, age life, size end, g drag, rot spin. */
  private static readonly W = 14;

  constructor(
    readonly cap: number,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    /** Lies flat on the ground (rings, splats) instead of standing up. */
    private readonly flat: boolean,
    /** Shrinks away over the end of its life (rings just stop). */
    private readonly fade: boolean,
  ) {
    this.f = new Float32Array(cap * Layer.W);
    this.mesh = new THREE.InstancedMesh(geometry, material, cap);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.setColorAt(0, this.c.set(0xffffff)); // makes its color buffer now, not on the first emit
  }

  emit(p: Emit): boolean {
    if (this.n >= this.cap) return false;
    const i = this.n++;
    const o = i * Layer.W;
    const f = this.f;
    f[o] = p.x;
    f[o + 1] = p.y;
    f[o + 2] = p.z;
    f[o + 3] = p.vx;
    f[o + 4] = p.vy;
    f[o + 5] = p.vz;
    f[o + 6] = 0;
    f[o + 7] = p.life;
    f[o + 8] = p.size;
    f[o + 9] = p.end;
    f[o + 10] = p.g;
    f[o + 11] = p.drag;
    f[o + 12] = p.rot;
    f[o + 13] = p.spin;
    this.mesh.setColorAt(i, p.color);
    return true;
  }

  step(dt: number): void {
    const f = this.f;
    const W = Layer.W;
    for (let i = 0; i < this.n; ) {
      const o = i * W;
      f[o + 6]! += dt;
      if (f[o + 6]! >= f[o + 7]!) {
        this.kill(i);
        continue; // the last one moved into slot i: step it too
      }
      const drag = Math.exp(-f[o + 11]! * dt);
      f[o + 3]! *= drag;
      f[o + 5]! *= drag;
      f[o + 4] = f[o + 4]! * drag - f[o + 10]! * dt;
      f[o] = f[o]! + f[o + 3]! * dt;
      f[o + 1] = f[o + 1]! + f[o + 4]! * dt;
      f[o + 2] = f[o + 2]! + f[o + 5]! * dt;
      f[o + 12] = f[o + 12]! + f[o + 13]! * dt;
      i++;
    }
    for (let i = 0; i < this.n; i++) {
      const o = i * W;
      const k = f[o + 6]! / f[o + 7]!;
      let s = f[o + 8]! + (f[o + 9]! - f[o + 8]!) * k;
      if (this.fade && k > 0.7) s *= (1 - k) / 0.3;
      this.e.set(this.flat ? -Math.PI / 2 : 0, f[o + 12]!, 0);
      this.q.setFromEuler(this.e);
      this.mesh.setMatrixAt(i, this.m.compose(this.pos.set(f[o]!, f[o + 1]!, f[o + 2]!), this.q, this.scale.setScalar(Math.max(1e-3, s))));
    }
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  clear(): void {
    this.n = 0;
    this.mesh.count = 0;
  }

  /** Swap-removes particle `i` (the last one takes its slot, color and all). */
  private kill(i: number): void {
    const last = --this.n;
    if (i === last) return;
    const W = Layer.W;
    this.f.copyWithin(i * W, last * W, last * W + W);
    this.mesh.getColorAt(last, this.c);
    this.mesh.setColorAt(i, this.c);
  }
}

/** An eighth note, about 1 m tall (scaled down per particle). */
function noteGeometry(): THREE.BufferGeometry {
  const head = new THREE.Shape();
  head.absellipse(0, 0, 0.32, 0.22, 0, Math.PI * 2, false, 0.4);
  const stem = new THREE.Shape();
  stem.moveTo(0.22, 0.05);
  stem.lineTo(0.3, 0.05);
  stem.lineTo(0.3, 1);
  stem.lineTo(0.22, 1);
  const flag = new THREE.Shape();
  flag.moveTo(0.3, 1);
  flag.quadraticCurveTo(0.75, 0.75, 0.6, 0.35);
  flag.quadraticCurveTo(0.6, 0.65, 0.3, 0.75);
  const g = new THREE.ShapeGeometry([head, stem, flag]);
  g.translate(0, -0.4, 0);
  return g;
}

const SPARK_COLORS: Record<Exclude<GrindSparksId, "rainbow">, string[]> = {
  classic: ["#ffd24d", "#ff9a1f", "#fff2b0"],
  fire: ["#ff3b1f", "#ff7a1a", "#ffc02e"],
  electric: ["#7fe8ff", "#ffffff", "#3aa0ff"],
};
const PAINT = ["#ff4fb8", "#2ec4b6", "#ffe14d", "#7a5cff", "#ff6b35"];

export class TrickFx {
  /** Add to the scene once. */
  readonly root = new THREE.Group();
  /** The settings: a style, or Random for a fresh pick each time (`picker`). */
  sparks: GrindSparksId | Random = "classic";
  landing: LandingFxId | Random = "dust";
  trail: AirTrailId | Random = "off";
  finisher: FinisherId | Random = "fireworks";
  /** What played last (or is playing): the grind's sparks and the air's trail hold for the whole grind or air. */
  readonly played: { sparks: GrindSparksId; landing: LandingFxId; trail: AirTrailId; finisher: FinisherId } = { sparks: "classic", landing: "dust", trail: "off", finisher: "fireworks" };
  /** The ribbon trail: a strip through the board's last positions, narrowing to its tail. */
  readonly ribbon: THREE.Mesh;
  private readonly ribbonPos = new Float32Array(TRAIL_POINTS * 2 * 3);
  /** The ribbon's points, oldest first: x y z age. */
  private readonly trailBuf = new Float32Array(TRAIL_POINTS * 4);
  private trailN = 0;
  private readonly layers: Record<LayerId, Layer>;
  /** The same pools as a list, made once, so walking them each frame allocates nothing. */
  private readonly pools: Layer[];
  private readonly rand = rng(118);
  private readonly color = new THREE.Color();
  private readonly p: Emit = { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, life: 1, size: 1, end: 0, g: 0, drag: 0, rot: 0, spin: 0, color: this.color };
  /** Sparks owed from past frames (a slow grind throws fewer than one a frame). */
  private owed = 0;
  private clock = 0;
  /** Last frame's mode, so a new grind or air picks its style once. */
  private lastMode = "";

  constructor(private readonly picker = new CosmeticPicker(Math.random)) {
    this.root.name = "trick-fx";
    const basic = (o: THREE.MeshBasicMaterialParameters = {}) => new THREE.MeshBasicMaterial({ toneMapped: false, ...o });
    this.layers = {
      spark: new Layer(FX_CAPS.spark, new THREE.OctahedronGeometry(1), basic(), false, true),
      puff: new Layer(FX_CAPS.puff, new THREE.IcosahedronGeometry(1), new THREE.MeshBasicMaterial(), false, true),
      ring: new Layer(FX_CAPS.ring, new THREE.RingGeometry(0.82, 1, 40), basic({ side: THREE.DoubleSide, transparent: true, opacity: 0.8, depthWrite: false }), true, false),
      splat: new Layer(FX_CAPS.splat, new THREE.CircleGeometry(1, 14), basic({ side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2 }), true, true),
      note: new Layer(FX_CAPS.note, noteGeometry(), basic({ side: THREE.DoubleSide }), false, true),
      // A speed line: a thin bar 1 m long down its z axis, yawed along the way you're going.
      streak: new Layer(FX_CAPS.streak, new THREE.BoxGeometry(0.025, 0.025, 1), basic(), false, true),
    };
    this.pools = Object.values(this.layers);
    for (const l of this.pools) this.root.add(l.mesh);

    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(this.ribbonPos, 3).setUsage(THREE.DynamicDrawUsage));
    const index: number[] = [];
    for (let i = 0; i < TRAIL_POINTS - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    g.setIndex(index);
    g.setDrawRange(0, 0);
    this.ribbon = new THREE.Mesh(g, basic({ color: "#ffffff", side: THREE.DoubleSide, transparent: true, opacity: 0.75, depthWrite: false }));
    this.ribbon.frustumCulled = false;
    this.ribbon.visible = false;
    this.root.add(this.ribbon);
  }

  /** Points in the ribbon trail right now. */
  get trailPoints(): number {
    return this.trailN;
  }

  /** Particles alive right now, across every pool. */
  get live(): number {
    let n = 0;
    for (const l of this.pools) n += l.n;
    return n;
  }

  /**
   * One frame: sparks while grinding, a burst for a landing cue in `cues`
   * (this frame's sim steps'), then everything moves on. `floor` is the
   * ground height under a point. Returns the finisher a big combo's landing
   * set off this frame, if any.
   */
  update(sk: FxSkater, cues: readonly Cue[], dt: number, floor: (x: number, z: number) => number, events: readonly SkaterEvent[] = []): FinisherId | null {
    this.clock += dt;
    const played = this.played;
    const fresh = sk.mode !== this.lastMode;
    this.lastMode = sk.mode;
    if (sk.mode === "grind") {
      // A grind's sparks: picked as it starts on Random, or the setting as it is now.
      if (this.sparks !== RANDOM) played.sparks = this.sparks;
      else if (fresh) played.sparks = this.picker.next("grindSparks");
      this.grindSparks(sk, dt);
    } else this.owed = 0;
    const flying = sk.mode === "air";
    if (flying) {
      if (this.trail !== RANDOM) played.trail = this.trail;
      else if (fresh) played.trail = this.picker.next("airTrail");
    }
    if (flying && played.trail === "comet") this.comet(sk);
    if (flying && played.trail === "speed") this.speedLines(sk);
    this.stepRibbon(sk, flying && played.trail === "ribbon", dt);
    for (const cue of cues)
      if (cue === "land" || cue === "sloppy" || cue === "perfect") {
        played.landing = this.picker.play("landingFx", this.landing);
        this.land(sk, cue === "perfect" ? PERFECT_BOOST : 1, floor);
      }
    let fired: FinisherId | null = null;
    for (const e of events)
      if (e.type === "landed" && e.points >= BIG_COMBO) {
        played.finisher = this.picker.play("finisher", this.finisher);
        if (played.finisher !== "off") fired = played.finisher;
      }
    if (fired === "fireworks") this.fireworks(sk);
    for (const l of this.pools) l.step(dt);
    return fired;
  }

  /** Drops every particle (a new map, a respawn you don't want trailing). */
  reset(): void {
    for (const l of this.pools) l.clear();
    this.owed = 0;
    this.trailN = 0;
    this.ribbon.visible = false;
    this.lastMode = "";
  }

  dispose(): void {
    this.reset();
    for (const l of this.pools) {
      l.mesh.geometry.dispose();
      (l.mesh.material as THREE.Material).dispose();
      l.mesh.dispose();
    }
    this.ribbon.geometry.dispose();
    (this.ribbon.material as THREE.Material).dispose();
  }

  private pick(list: readonly string[]): THREE.Color {
    return this.color.set(list[Math.floor(this.rand() * list.length)]!);
  }

  private grindSparks(sk: FxSkater, dt: number): void {
    const speed = Math.hypot(sk.vx, sk.vz);
    this.owed += speed * SPARK_RATE * dt;
    const r = this.rand;
    // Thrown back off the trucks, away from the way you're going.
    const bx = speed > 0.1 ? -sk.vx / speed : 0;
    const bz = speed > 0.1 ? -sk.vz / speed : 0;
    const p = this.p;
    for (; this.owed >= 1; this.owed--) {
      const back = speed * (0.15 + r() * 0.2);
      p.x = sk.x + (r() - 0.5) * 0.3;
      p.y = sk.y + TRUCK_Y;
      p.z = sk.z + (r() - 0.5) * 0.3;
      p.vx = bx * back + (r() - 0.5) * 2.4;
      p.vy = 1 + r() * 2.2;
      p.vz = bz * back + (r() - 0.5) * 2.4;
      p.rot = r() * 6;
      p.spin = (r() - 0.5) * 20;
      switch (this.played.sparks) {
        case "classic":
          this.set(0.25 + r() * 0.2, 0.05, 0.012, 9.8, 1);
          this.pick(SPARK_COLORS.classic);
          break;
        case "rainbow":
          this.set(0.35 + r() * 0.25, 0.055, 0.015, 6, 1.5);
          this.color.setHSL((this.clock * 0.8 + r() * 0.15) % 1, 1, 0.6);
          break;
        case "fire":
          // Embers: bigger, slower, and they float up instead of falling.
          p.vx *= 0.5;
          p.vz *= 0.5;
          p.vy = 0.4 + r() * 0.8;
          this.set(0.45 + r() * 0.3, 0.06, 0.01, -2.5, 2.5);
          this.pick(SPARK_COLORS.fire);
          break;
        case "electric":
          // Crackle: fast, short and blue-white.
          p.vx *= 2;
          p.vz *= 2;
          p.vy *= 1.4;
          this.set(0.12 + r() * 0.1, 0.045, 0.02, 4, 0.5);
          this.pick(SPARK_COLORS.electric);
          break;
      }
      this.layers.spark.emit(p);
    }
  }

  /** Ages the ribbon's points, adds the board's spot while `adding`, and rebuilds the strip. */
  private stepRibbon(sk: FxSkater, adding: boolean, dt: number): void {
    const b = this.trailBuf;
    let drop = 0;
    for (let i = 0; i < this.trailN; i++) {
      b[i * 4 + 3]! += dt;
      if (b[i * 4 + 3]! >= TRAIL_LIFE) drop = i + 1;
    }
    if (adding && this.trailN - drop >= TRAIL_POINTS) drop = this.trailN - TRAIL_POINTS + 1;
    if (drop > 0) {
      b.copyWithin(0, drop * 4, this.trailN * 4);
      this.trailN -= drop;
    }
    if (adding) {
      const o = this.trailN++ * 4;
      b[o] = sk.x;
      b[o + 1] = sk.y + BOARD_Y;
      b[o + 2] = sk.z;
      b[o + 3] = 0;
    }
    const n = this.trailN;
    this.ribbon.visible = n >= 2;
    this.ribbon.geometry.setDrawRange(0, n >= 2 ? (n - 1) * 6 : 0);
    if (n < 2) return;
    const pos = this.ribbonPos;
    for (let i = 0; i < n; i++) {
      // Across the way the trail runs, flat on the ground's plane; narrowing to nothing at the tail.
      const a = Math.max(0, i - 1);
      const c = Math.min(n - 1, i + 1);
      let dx = b[c * 4]! - b[a * 4]!;
      let dz = b[c * 4 + 2]! - b[a * 4 + 2]!;
      const len = Math.hypot(dx, dz);
      if (len < 1e-4) {
        dx = 1;
        dz = 0;
      } else {
        dx /= len;
        dz /= len;
      }
      const w = (TRAIL_WIDTH / 2) * (1 - b[i * 4 + 3]! / TRAIL_LIFE) * (i / (n - 1));
      const o = i * 6;
      pos[o] = b[i * 4]! - dz * w;
      pos[o + 1] = b[i * 4 + 1]!;
      pos[o + 2] = b[i * 4 + 2]! + dx * w;
      pos[o + 3] = b[i * 4]! + dz * w;
      pos[o + 4] = b[i * 4 + 1]!;
      pos[o + 5] = b[i * 4 + 2]! - dx * w;
    }
    (this.ribbon.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
  }

  /** A comet's tail: glowing bits left hanging behind the board, drifting and shrinking. */
  private comet(sk: FxSkater): void {
    const r = this.rand;
    const p = this.p;
    for (let i = 0; i < 2; i++) {
      p.x = sk.x + (r() - 0.5) * 0.15;
      p.y = sk.y + BOARD_Y + (r() - 0.5) * 0.15;
      p.z = sk.z + (r() - 0.5) * 0.15;
      p.vx = (r() - 0.5) * 0.4;
      p.vy = (r() - 0.5) * 0.4;
      p.vz = (r() - 0.5) * 0.4;
      p.rot = r() * 6;
      p.spin = (r() - 0.5) * 6;
      this.set(0.4 + r() * 0.2, 0.09, 0.01, 0, 2);
      this.color.setHSL(0.55 + r() * 0.1, 1, 0.65 + r() * 0.25);
      this.layers.spark.emit(p);
    }
  }

  /** Comic speed lines: short bars left in the air around the rider, pointing the way they fly. */
  private speedLines(sk: FxSkater): void {
    const r = this.rand;
    const p = this.p;
    const speed = Math.hypot(sk.vx, sk.vz);
    if (speed < 1 || r() > 0.6) return;
    const yaw = Math.atan2(sk.vx, sk.vz);
    // Off to one side and a little behind, so they frame the rider rather than cover them.
    const side = (r() < 0.5 ? -1 : 1) * (0.35 + r() * 0.5);
    p.x = sk.x + (sk.vz / speed) * side - (sk.vx / speed) * 0.6;
    p.y = sk.y + 0.2 + r() * 1.4;
    p.z = sk.z - (sk.vx / speed) * side - (sk.vz / speed) * 0.6;
    p.vx = p.vy = p.vz = 0;
    p.rot = yaw;
    p.spin = 0;
    this.set(0.22, 0.6 + r() * 0.6, 0.2, 0, 0);
    this.color.set("#ffffff");
    this.layers.streak.emit(p);
  }

  /** Three bursts in party colors, ahead of the rider where the chase camera is looking. */
  private fireworks(sk: FxSkater): void {
    const r = this.rand;
    const p = this.p;
    const speed = Math.hypot(sk.vx, sk.vz);
    const fx = speed > 0.1 ? sk.vx / speed : 0;
    const fz = speed > 0.1 ? sk.vz / speed : 1;
    for (let burst = 0; burst < 3; burst++) {
      const ahead = 4 + r() * 2;
      const side = (burst - 1) * 2.5 + (r() - 0.5);
      const cx = sk.x + fx * ahead + fz * side;
      const cy = sk.y + 2.5 + r() * 1.2;
      const cz = sk.z + fz * ahead - fx * side;
      const color = FIREWORK_COLORS[Math.floor(r() * FIREWORK_COLORS.length)]!;
      for (let i = 0; i < 36; i++) {
        // Even-ish over a sphere.
        const u = r() * 2 - 1;
        const t = r() * Math.PI * 2;
        const k = Math.sqrt(1 - u * u) * 5;
        p.x = cx;
        p.y = cy;
        p.z = cz;
        p.vx = Math.cos(t) * k;
        p.vy = u * 5;
        p.vz = Math.sin(t) * k;
        p.rot = r() * 6;
        p.spin = (r() - 0.5) * 10;
        this.set(1 + r() * 0.4, 0.12, 0.03, 3, 1.6);
        this.color.set(color);
        this.layers.spark.emit(p);
      }
    }
  }

  private set(life: number, size: number, end: number, g: number, drag: number): void {
    const p = this.p;
    p.life = life;
    p.size = size;
    p.end = end;
    p.g = g;
    p.drag = drag;
  }

  private land(sk: FxSkater, boost: number, floor: (x: number, z: number) => number): void {
    const r = this.rand;
    const p = this.p;
    const count = (n: number) => Math.round(n * boost);
    switch (this.played.landing) {
      case "dust":
        for (let i = 0, n = count(12); i < n; i++) {
          const a = (i / n) * Math.PI * 2 + r() * 0.4;
          const out = (1.2 + r() * 1.4) * boost;
          p.x = sk.x + Math.cos(a) * 0.25;
          p.y = sk.y + 0.08;
          p.z = sk.z + Math.sin(a) * 0.25;
          p.vx = Math.cos(a) * out + sk.vx * 0.2;
          p.vy = 0.3 + r() * 0.5;
          p.vz = Math.sin(a) * out + sk.vz * 0.2;
          p.rot = r() * 6;
          p.spin = 0;
          this.set(0.5 + r() * 0.25, 0.06, 0.2 * boost, -0.6, 4);
          this.color.set("#d8cbb4").offsetHSL(0, 0, (r() - 0.5) * 0.1);
          this.layers.puff.emit(p);
        }
        break;
      case "ring":
        p.x = sk.x;
        p.y = sk.y + 0.04;
        p.z = sk.z;
        p.vx = p.vy = p.vz = 0;
        p.rot = 0;
        p.spin = 0;
        this.set(0.4, 0.2, 2, 0, 0);
        this.color.set("#ffffff");
        this.layers.ring.emit(p);
        if (boost > 1) {
          // A Perfect sends a second, gold ring out wider behind the first.
          this.set(0.55, 0.1, 3.4, 0, 0);
          this.color.set("#ffe14d");
          this.layers.ring.emit(p);
        }
        break;
      case "splat":
        for (let i = 0, n = count(7); i < n; i++) {
          const a = r() * Math.PI * 2;
          const d = r() * 0.9 * boost;
          p.x = sk.x + Math.cos(a) * d;
          p.z = sk.z + Math.sin(a) * d;
          p.y = floor(p.x, p.z) + 0.02;
          p.vx = p.vy = p.vz = 0;
          p.rot = r() * 6;
          p.spin = 0;
          const size = 0.18 + r() * 0.25;
          this.set(1.1 + r() * 0.4, size * 0.3, size, 0, 0);
          this.pick(PAINT);
          this.layers.splat.emit(p);
        }
        break;
      case "notes":
        for (let i = 0, n = count(4); i < n; i++) {
          const a = r() * Math.PI * 2;
          p.x = sk.x + Math.cos(a) * 0.3;
          p.y = sk.y + 0.6 + r() * 0.5;
          p.z = sk.z + Math.sin(a) * 0.3;
          p.vx = Math.cos(a) * 0.6;
          p.vy = 1.2 + r() * 0.6;
          p.vz = Math.sin(a) * 0.6;
          p.rot = r() * 6;
          p.spin = (r() - 0.5) * 4;
          this.set(1 + r() * 0.3, 0.22, 0.3, 0, 1.2);
          this.pick(PAINT);
          this.layers.note.emit(p);
        }
        break;
    }
  }
}
